'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { createBrowserClient } from '@/utils/supabase/client';
import { useQuickLogin } from './QuickLoginContext';
import { FiX, FiMail, FiLoader, FiGithub } from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

export default function QuickLoginModal() {
  const { isOpen, closeModal } = useQuickLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState<'password' | 'magic-link'>('password');
  
  const router = useRouter();
  const supabase = createBrowserClient();
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Focus email input when modal opens
  useEffect(() => {
    if (isOpen && emailInputRef.current) {
      setTimeout(() => emailInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Load last used method from localStorage
  useEffect(() => {
    const lastMethod = localStorage.getItem('quickLoginMethod') as 'password' | 'magic-link' | null;
    if (lastMethod) {
      setAuthMethod(lastMethod);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (authMethod === 'password') {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          toast.error(error.message);
        } else {
          toast.success('Logged in successfully!');
          localStorage.setItem('quickLoginMethod', 'password');
          closeModal();
          router.push('/dashboard');
        }
      } else {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          },
        });

        if (error) {
          toast.error(error.message);
        } else {
          toast.success('Check your email for the magic link!');
          localStorage.setItem('quickLoginMethod', 'magic-link');
          closeModal();
        }
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'github' | 'google') => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/api/auth/callback`,
        },
      });

      if (error) {
        toast.error(error.message);
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-scrim/50 transition-opacity"
        onClick={closeModal}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6 animate-fade-in">
          {/* Close button */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 text-base-content/50 hover:text-base-content transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-base-content">Quick Login</h2>
            <p className="text-sm text-base-content/60 mt-1">
              Press <kbd className="kbd kbd-sm">Cmd+K</kbd> to open anytime
            </p>
          </div>

          {/* Auth method tabs */}
          <div className="tabs tabs-boxed mb-6">
            <button
              className={`tab ${authMethod === 'password' ? 'tab-active' : ''}`}
              onClick={() => setAuthMethod('password')}
            >
              Password
            </button>
            <button
              className={`tab ${authMethod === 'magic-link' ? 'tab-active' : ''}`}
              onClick={() => setAuthMethod('magic-link')}
            >
              Magic Link
            </button>
          </div>

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="quick-email" className="label">
                <span className="label-text">Email</span>
              </label>
              <input
                ref={emailInputRef}
                id="quick-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input input-bordered w-full"
                placeholder="you@example.com"
                required
                disabled={isLoading}
              />
            </div>

            {authMethod === 'password' && (
              <div>
                <label htmlFor="quick-password" className="label">
                  <span className="label-text">Password</span>
                </label>
                <input
                  id="quick-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input input-bordered w-full"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin mr-2" />
                  {authMethod === 'password' ? 'Signing in...' : 'Sending link...'}
                </>
              ) : (
                <>
                  <FiMail className="w-4 h-4 mr-2" />
                  {authMethod === 'password' ? 'Sign In' : 'Send Magic Link'}
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="divider my-6">OR</div>

          {/* Social login buttons */}
          <div className="space-y-3">
            <button
              onClick={() => handleSocialLogin('google')}
              className="btn btn-outline w-full"
              disabled={isLoading}
            >
              <FaGoogle className="w-4 h-4 mr-2" />
              Continue with Google
            </button>
            <button
              onClick={() => handleSocialLogin('github')}
              className="btn btn-outline w-full"
              disabled={isLoading}
            >
              <FiGithub className="w-4 h-4 mr-2" />
              Continue with GitHub
            </button>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-base-content/60">
              Don&apos;t have an account?{' '}
              <a href="/signin" className="link link-primary">
                Sign up
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}